import React, {useMemo} from 'react';
import _ from 'underscore';

import {sum} from 'BenchlingUtil';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameRow} from 'data-frames/DataFrameTypes';
import {stringAccessor} from 'data-frames/util/AccessorUtils';
import addColumn from 'data-frames/util/addColumn';
import {createAccessor, DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import filterColumns from 'data-frames/util/filterColumns';
import filterRows from 'data-frames/util/filterRows';
import {sortDataFrameByAccessors} from 'data-frames/util/sortDataFrame';
import updateColumnDef from 'data-frames/util/updateColumnDef';
import {
  AnalyticsVisualizationAxisScale,
  AnalyticsVisualizationBarChartType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';

import {ShapValuesColumnsMapping} from '../ParameterOptimizationTypes';
import {
  getBaseEntitiesToBaseFeaturesMap,
  makeParameterOptimizationFeature,
} from '../ParameterOptimizationUtils';
import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';
import {
  PARAMETER_COLUMN_DEF,
  SHAP_VALUE_COLUMN_SYSTEM_NAME,
  SHAP_VALUE_Y_AXIS_LABEL,
} from './ModelingDashboardConstants';

export const LOCAL_EXPLANATION_BLOCK_TITLE = 'Parameter contribution by data point';
const LOCAL_EXPLANATION_BLOCK_DESCRIPTION =
  'The parameter value is plotted against how much it contributed to the experimental output.';

/** @package */
export const MAX_FEATURES_TO_VISUALIZE = 20;
/** @package */
export const VISUALIZATION_TYPE = VisualizationType.BAR_CHART;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  aggregationType: null,
  barChartType: AnalyticsVisualizationBarChartType.STANDARD,
  errorBarType: null,
  sortDirection: null,
  seriesColumnName: null,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
};

/** @package */
export const HIDDEN_POSITIVE_SHAP_VALUES_LABEL = 'Remaining (+) features';
/** @package */
export const HIDDEN_NEGATIVE_SHAP_VALUES_LABEL = 'Remaining (-) features';

/**
 * Add the `FEATURE_COLUMN_DEF` column to the filter `shapValues` data frame. This is generated using
 * `makeParameterOptimizationFeature`'s `displayName`.
 */
function addFeatureColumn(
  dataFrame: DataFrame,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>
): DataFrame {
  const baseEntitiesToBaseFeatureMap = getBaseEntitiesToBaseFeaturesMap(
    dataFrame,
    baseEntitiesAccessor,
    baseFeaturesAccessor
  );

  return addColumn(
    dataFrame,
    PARAMETER_COLUMN_DEF,
    (row) =>
      makeParameterOptimizationFeature(
        baseEntitiesAccessor(row)[0],
        baseFeaturesAccessor(row)[0],
        baseEntitiesToBaseFeatureMap
      ).displayName
  );
}

/**
 * Given a `selectedDataPointIdShapValues` data frame, find the `MAX_FEATURES_TO_VISUALIZE` features with
 * the highest absolute shap values, and summarize the rest into two rows - one with all the features with positive
 * shap values, and one with all the features with negative shap values
 *
 *
 * To generate this, we follow these steps:
 * 1. Sort rows by absolute shap values and then by feature name to break ties
 * 2. Keep the first 20 rows, and partition the rest into 2 arrays by positive and negative shap values
 * 3. Sum the 2 arrays to get the aggregated shap value for each
 * 4. Add 2 rows with the `HIDDEN_POSITIVE_SHAP_VALUES_LABEL` and `HIDDEN_NEGATIVE_SHAP_VALUES_LABEL` labels
 * as feature names and the corresponding aggregated shap value as the shap value.
 *
 *  @package
 * */
export function summarizeLessInfluentialFeatures(
  selectedDataPointIdShapValues: DataFrame,
  columnsMapping: Pick<ShapValuesColumnsMapping, 'shapValue'>,
  featureAccessor: DataFrameValueAccessor<string>,
  shapValueAccessor: DataFrameValueAccessor<number>
): DataFrame {
  const sortedSelectedDataPointIdShapValues = sortDataFrameByAccessors(selectedDataPointIdShapValues, [
    (row: DataFrameRow) => -1 * Math.abs(shapValueAccessor(row)),
    featureAccessor,
  ]);

  const lessInfluentialFeatureRows =
    sortedSelectedDataPointIdShapValues.rows.slice(MAX_FEATURES_TO_VISUALIZE);
  const [positiveLessInfluentialFeatureRows, negativeLessInfluentialFeatureRows] = _.partition(
    lessInfluentialFeatureRows,
    (row: DataFrameRow) => shapValueAccessor(row) >= 0
  );

  const aggregatedPositiveLessInfluentialShapValue = sum(
    positiveLessInfluentialFeatureRows.map((row) => shapValueAccessor(row))
  );
  const aggregatedNegativeLessInfluentialShapValue = sum(
    negativeLessInfluentialFeatureRows.map((row) => shapValueAccessor(row))
  );

  return {
    ...sortedSelectedDataPointIdShapValues,
    rows: [
      ...sortedSelectedDataPointIdShapValues.rows.slice(0, 20),
      ...(positiveLessInfluentialFeatureRows.length > 0
        ? [
            {
              [PARAMETER_COLUMN_DEF.systemName]: HIDDEN_POSITIVE_SHAP_VALUES_LABEL,
              [columnsMapping.shapValue]: aggregatedPositiveLessInfluentialShapValue,
              [SystemColumnName.IS_EXCLUDED]: false,
              [SystemColumnName.ROW_ID]: HIDDEN_POSITIVE_SHAP_VALUES_LABEL,
            },
          ]
        : []),
      ...(negativeLessInfluentialFeatureRows.length > 0
        ? [
            {
              [PARAMETER_COLUMN_DEF.systemName]: HIDDEN_NEGATIVE_SHAP_VALUES_LABEL,
              [columnsMapping.shapValue]: aggregatedNegativeLessInfluentialShapValue,
              [SystemColumnName.IS_EXCLUDED]: false,
              [SystemColumnName.ROW_ID]: HIDDEN_NEGATIVE_SHAP_VALUES_LABEL,
            },
          ]
        : []),
    ],
  };
}

/**
 * Given a shap values data frame and a selected `dataPointId`, return the data frame to render
 * the `dataPointId`'s local explanation chart.
 *
 * To generate the visualization data frame, perform the following transformations:
 * 1. Filter the `shapValuesDataFrame` to only included rows where the corresponding `dataPointId` equals `selectedDataPointId`
 * 2. Remove all columns that are not the `featureName` and `shapValue`.
 * 3. Summarize the influence of every feature except the `MAX_FEATURES_TO_VISUALIZE` most influential*
 * 4. Sort the data frame by `shapValue`s to to show x axis as negative -> positive shap values
 * 5. Rename display name of `shapValue` to `SHAP_VALUE_Y_AXIS_LABEL`
 *
 * * most influential here means they have the highest absolute shap values.
 *
 * @package
 */
export function makeVisualizationData(
  shapValuesDataFrame: DataFrame,
  selectedDataPointId: string,
  columnsMapping: Pick<ShapValuesColumnsMapping, 'shapValue'>,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>,
  dataPointIdAccessor: DataFrameValueAccessor<string>,
  shapValueAccessor: DataFrameValueAccessor<number>
): DataFrame {
  let selectedDataPointIdShapValues = filterRows(
    shapValuesDataFrame,
    (row) => dataPointIdAccessor(row) === selectedDataPointId
  );

  selectedDataPointIdShapValues = addFeatureColumn(
    selectedDataPointIdShapValues,
    baseEntitiesAccessor,
    baseFeaturesAccessor
  );

  selectedDataPointIdShapValues = filterColumns(selectedDataPointIdShapValues, (column) =>
    [PARAMETER_COLUMN_DEF.systemName, columnsMapping.shapValue].includes(column.systemName)
  );

  const featureAccessors = stringAccessor(
    createAccessor(selectedDataPointIdShapValues, PARAMETER_COLUMN_DEF.systemName).accessor
  );

  selectedDataPointIdShapValues = summarizeLessInfluentialFeatures(
    selectedDataPointIdShapValues,
    columnsMapping,
    featureAccessors,
    shapValueAccessor
  );

  selectedDataPointIdShapValues = sortDataFrameByAccessors(selectedDataPointIdShapValues, [
    shapValueAccessor,
  ]);

  return updateColumnDef(selectedDataPointIdShapValues, columnsMapping.shapValue, {
    displayName: SHAP_VALUE_Y_AXIS_LABEL,
  });
}

export interface LocalExplanationBlockProps extends SharedModelingBlockProps {
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>;
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>;
  dataFrame: DataFrame;
  columnsMapping: ShapValuesColumnsMapping;
  dataPointIdAccessor: DataFrameValueAccessor<string>;
  resolveDataPointId: (dataPointId: string) => string;
  shapValueAccessor: DataFrameValueAccessor<number>;
  selectedDataPointId: string;
}

export default function LocalExplanationBlock({
  baseEntitiesAccessor,
  baseFeaturesAccessor,
  dataFrame,
  columnsMapping,
  dataPointIdAccessor,
  resolveDataPointId,
  selectedDataPointId,
  shapValueAccessor,
  ...sharedModelingBlockProps
}: LocalExplanationBlockProps): JSX.Element {
  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, {
    ...BASE_VISUALIZATION_PARAMETERS,
    xAxisColumnName: PARAMETER_COLUMN_DEF.systemName,
    yAxisColumnName: SHAP_VALUE_COLUMN_SYSTEM_NAME,
  });

  const visualizationShapValues = useMemo(
    () =>
      makeVisualizationData(
        dataFrame,
        selectedDataPointId,
        columnsMapping,
        baseEntitiesAccessor,
        baseFeaturesAccessor,
        dataPointIdAccessor,
        shapValueAccessor
      ),
    [
      dataFrame,
      selectedDataPointId,
      columnsMapping,
      baseEntitiesAccessor,
      baseFeaturesAccessor,
      dataPointIdAccessor,
      shapValueAccessor,
    ]
  );

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={LOCAL_EXPLANATION_BLOCK_TITLE}
      blockDescription={LOCAL_EXPLANATION_BLOCK_DESCRIPTION}
      inputDataFrame={visualizationShapValues}
      visualizationConfig={visualizationConfig}
    />
  );
}
